<template>
  <div>
    <b-card>
      <b-overlay
        :show="loading"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <!-- Title and subtitle (left aligned) -->
        <div class="mb-1 mt-1 text-left">
          <h3
            class="font-weight-bolder mb-1"
          >
            Map Provider Configuration
          </h3>
          <div
            class="text-muted"
          >
            Select and configure your preferred map service provider
          </div>
        </div>

        <b-row
          class="py-4 justify-content-start"
        >
          <b-col
            cols="12"
            md="8"
            lg="6"
            class="text-left"
          >
            <b-form-group
              label="Name"
              label-for="map-name"
            >
              <div class="d-flex align-items-center">
                <b-form-select
                  id="map-name"
                  v-model="selectedId"
                  :options="nameOptions"
                  :disabled="loading"
                  class="mb-0 flex-grow-1"
                  @change="onNameChange"
                />
                <b-button
                  variant="outline-primary"
                  class="ml-2"
                  size="md"
                  data-test="add-map-provider-btn"
                  @click="openAddModal"
                >
                  <feather-icon
                    icon="PlusIcon"
                  />
                </b-button>
              </div>
            </b-form-group>

            <b-form-group
              label="API Key"
              label-for="api-key"
            >
              <b-form-input
                id="api-key"
                v-model="apiKey"
                readonly
                class="mb-3"
                placeholder="API Key will appear here"
              />
            </b-form-group>

            <div
              class="d-flex justify-content-start mb-3"
            >
              <b-button
                variant="info"
                class="mr-2"
                :disabled="!selectedId || testingLoading"
                @click="testConnection"
              >
                <span v-if="testingLoading">Testing…</span>
                <span v-else>Test Connection</span>
              </b-button>

              <b-button
                variant="danger"
                class="mr-2"
                :disabled="loading"
                data-test="delete-api-keys-btn"
                @click="openDeleteModal"
              >
                <feather-icon
                  icon="Trash2Icon"
                  class="mr-50"
                />
                Delete API Keys
              </b-button>

              <b-button
                variant="primary"
                :disabled="savingLoading || !selectedId"
                @click="saveConfigurations"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-50"
                />
                <span v-if="savingLoading">Saving…</span>
                <span v-else>Save Configurations</span>
              </b-button>
            </div>
            <!-- Delete API Keys modal -->
            <b-modal
              id="delete-map-modal"
              v-model="showDeleteModal"
              title="Delete API Keys"
              hide-footer
              centered
            >
              <div
                v-if="deleteApiKeys.length === 0"
                class="text-center text-muted py-3"
              >
                No API keys found.
              </div>
              <b-list-group v-else>
                <b-list-group-item
                  v-for="item in deleteApiKeys"
                  :key="item.id"
                  class="d-flex align-items-center justify-content-between"
                >
                  <span>{{ item.name }}</span>
                  <span>
                    <feather-icon
                      v-if="item.is_active"
                      icon="CheckCircleIcon"
                      class="text-success"
                      title="Active"
                    />
                    <b-button
                      v-else
                      variant="link"
                      class="p-0 text-danger"
                      :disabled="deleteLoadingId === item.id"
                      data-test="delete-api-key-btn"
                      @click="deleteApiKey(item.id)"
                    >
                      <feather-icon icon="Trash2Icon" />
                    </b-button>
                  </span>
                </b-list-group-item>
              </b-list-group>
              <div class="d-flex justify-content-end mt-3">
                <b-button
                  variant="secondary"
                  @click="closeDeleteModal"
                >Close</b-button>
              </div>
            </b-modal>
          </b-col>
        </b-row>

        <!-- Add provider modal -->
        <b-modal
          id="add-map-modal"
          v-model="showAddModal"
          title="Add Map Provider"
          hide-footer
          centered
        >
          <b-form @submit.prevent="saveNewConfig">
            <b-form-group
              label="Name"
              label-for="new-name"
            >
              <b-form-input
                id="new-name"
                v-model.trim="newConfigName"
                required
                autocomplete="off"
                data-test="new-map-name"
              />
            </b-form-group>

            <b-form-group
              label="API Key"
              label-for="new-api-key"
            >
              <b-form-input
                id="new-api-key"
                v-model.trim="newConfigApiKey"
                required
                autocomplete="off"
                data-test="new-map-api-key"
              />
            </b-form-group>

            <div class="d-flex justify-content-end">
              <b-button
                variant="secondary"
                class="mr-2"
                @click="closeAddModal"
              >
                Cancel
              </b-button>
              <b-button
                variant="primary"
                :disabled="addSaving"
                type="submit"
                @click="saveNewConfig"
              >
                <span v-if="addSaving">Saving…</span>
                <span v-else>Save</span>
              </b-button>
            </div>
          </b-form>
        </b-modal>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BOverlay,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BButton,
  BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const MapRepository = RepositoryFactory.get('mapConfig')

export default {
  directives: { Ripple },
  components: {
    BCard,
    BRow,
    BCol,
    BOverlay,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BButton,
    BModal,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      testingLoading: false,
      savingLoading: false,
      addSaving: false,
      nameOptions: [],
      selectedId: null,
      apiKey: '',
      showAddModal: false,
      newConfigName: '',
      newConfigApiKey: '',
      showDeleteModal: false,
      deleteApiKeys: [],
      deleteLoadingId: null,
    }
  },
  created() {
    this.loadApiKeys()
    this.loadActiveKey()
  },
  methods: {
    async loadApiKeys() {
      if (!MapRepository || !MapRepository.listApiKeys) return
      this.loading = true
      try {
        const resp = await MapRepository.listApiKeys()
        const data = resp?.data?.data || []
        this.nameOptions = data.map(item => ({ value: item.id, text: item.name }))
        this.deleteApiKeys = data.map(item => ({ id: item.id, name: item.name, is_active: !!item.is_active }))
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },
    openDeleteModal() {
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      this.showDeleteModal = false
    },
    async deleteApiKey(id) {
      if (!id || !MapRepository || !MapRepository.deleteApiKey) return
      this.deleteLoadingId = id
      try {
        await MapRepository.deleteApiKey(id)
        this.showSuccessMessage('API key deleted.')
        await this.loadApiKeys()
        // If deleted key was selected, clear selection
        if (this.selectedId === id) {
          this.selectedId = null
          this.apiKey = ''
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.deleteLoadingId = null
      }
    },
    async loadActiveKey() {
      if (!MapRepository || !MapRepository.getActiveApiKey) return
      this.loading = true
      try {
        const resp = await MapRepository.getActiveApiKey()
        const active = resp?.data
        if (active && active.data) {
          this.selectedId = active.data.id
          if (resp.data.key) {
            this.apiKey = resp.data.key
          } else {
            await this.loadKeyById(this.selectedId)
          }
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },
    async loadKeyById(id) {
      if (!id || !MapRepository || !MapRepository.getApiKeyById) return
      try {
        const resp = await MapRepository.getApiKeyById(id)
        if (resp?.data?.key) {
          this.apiKey = resp.data.key
        } else {
          this.apiKey = resp.data.key || ''
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async onNameChange(id) {
      if (!id) {
        this.apiKey = ''
        return
      }
      await this.loadKeyById(id)
    },
    openAddModal() {
      this.newConfigName = ''
      this.newConfigApiKey = ''
      this.showAddModal = true
    },
    closeAddModal() {
      this.showAddModal = false
    },
    async saveNewConfig() {
      if (!this.newConfigName.trim() || !this.newConfigApiKey.trim()) {
        this.showErrorMessage('Name and API Key are required.')
        return
      }
      if (!MapRepository || !MapRepository.createApiKey) {
        this.showErrorMessage('Save endpoint not available.')
        return
      }
      this.addSaving = true
      try {
        const payload = { name: this.newConfigName.trim(), key: this.newConfigApiKey.trim() }
        const resp = await MapRepository.createApiKey(payload)
        const respData = resp?.data
        const created = respData?.data
        const createdId = created?.id || respData?.id
        const createdKey = respData?.key || this.newConfigApiKey.trim()
        if (createdId) {
          this.nameOptions.push({ value: createdId, text: created.name || this.newConfigName.trim() })
          this.selectedId = createdId
          this.apiKey = createdKey
        } else {
          await this.loadApiKeys()
        }
        this.showSuccessMessage(respData?.message || 'Configuration saved.')
        this.closeAddModal()
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to save configuration.'
        this.showErrorMessage(msg)
      } finally {
        this.addSaving = false
      }
    },
    async testConnection() {
      if (!this.selectedId) return
      if (!MapRepository || !MapRepository.testApiKey) {
        this.showErrorMessage('Test endpoint not available.')
        return
      }
      this.testingLoading = true
      try {
        const resp = await MapRepository.testApiKey(this.selectedId)
        const msg = resp?.data?.message || 'Test completed.'
        this.showSuccessMessage(msg)
      } catch (error) {
        const msg = error.response?.data?.message || 'Test failed.'
        this.showErrorMessage(msg)
      } finally {
        this.testingLoading = false
      }
    },
    async saveConfigurations() {
      if (!this.selectedId) return
      if (!MapRepository || !MapRepository.updateApiKey) {
        this.showErrorMessage('Save endpoint not available.')
        return
      }
      this.savingLoading = true
      try {
        const resp = await MapRepository.updateApiKey(this.selectedId)
        const msg = resp?.data?.message || 'Configurations saved.'
        await this.loadApiKeys()
        await this.loadActiveKey()
        this.showSuccessMessage(msg)
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to save configurations.'
        this.showErrorMessage(msg)
      } finally {
        this.savingLoading = false
      }
    },
    convertAndNotifyError(e) {
      const msg = e.response?.data?.message || e.message || 'Something went wrong'
      this.showErrorMessage(msg)
    },
  },
}
</script>

<style scoped>
.mx-2 {
  margin-left: .5rem;
  margin-right: .5rem;
}
.text-success {
  color: #28c76f !important;
}
</style>
